import { CartItem } from "@omni/types/api/cart/bff/shared/cartItem.model";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { hasWindow } from "@omni/utils/dom.utils";
import { isDefined } from "@omni/utils/helper.utils";
import { pick } from "lodash-es";
import queryString from "query-string";
import { createContext, use, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";

import { getCart } from "@/redux/modules/api/data/cart/cart.selectors";
import { productConfigurations } from "@/utils/productConfiguration/productConfiguration.utils";

import { useProductDetail } from "../hooks/useProductDetail.hook";
import { useProductConfigurationContext } from "./productConfiguration.context";

type CartProductContextValue = {
    cartProduct: CartItem | undefined;
};

export type CartProductContextProviderProps = {};

export const CartProductContext = createContext<
    CartProductContextValue | undefined
>(undefined);

CartProductContext.displayName = "CartProductContext";

export const CartProductContextProvider = ({
    children
}: RequireChildrenFor<CartProductContextProviderProps>) => {
    const shoppingCart = useSelector(getCart);
    const { updateProductDetail } = useProductDetail();
    const { setConfiguration } = useProductConfigurationContext();

    const qpCartEntry = queryString.parse(!hasWindow() ? "" : location.search)
        ?.cartid as string;
    const cartProduct = shoppingCart?.items.find((i) => i.id === qpCartEntry);

    const productConfiguration = useMemo(() => {
        return pick(
            cartProduct,
            productConfigurations.reduce(
                (acc, pc) => [...acc, ...pc.configPaths],
                [] as string[]
            )
        );
    }, [cartProduct]);

    useEffect(() => {
        if (
            !productConfiguration ||
            Object.keys(productConfiguration || {}).length === 0
        ) {
            return;
        }
        setConfiguration(productConfiguration);
    }, [productConfiguration, setConfiguration]);

    useEffect(() => {
        if (!cartProduct) {
            return;
        }

        updateProductDetail({
            quantity: cartProduct.quantity,
            deliveryType: cartProduct.deliveryType
        });
    }, [cartProduct, updateProductDetail]);

    return (
        <CartProductContext value={{ cartProduct }}>
            {children}
        </CartProductContext>
    );
};

export const useCartProductContext = (): CartProductContextValue => {
    const context = use<CartProductContextValue | undefined>(
        CartProductContext
    );
    if (!isDefined(context)) {
        throw new Error(`${CartProductContext.displayName} is undefined.`);
    }
    return context;
};
